import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { httpsCallable } from '../lib/callable';
import { functions } from '../firebase';
import { useAuth } from './AuthContext';

export interface CustomerBranding {
  customerId: string;
  customerName: string;
  productName: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  domains: string[];
  enabled: boolean;
}

export const defaultBranding: CustomerBranding = {
  customerId: 'documentspark', customerName: 'DocumentSpark', productName: 'DocumentSpark',
  logoUrl: '/logo.png', primaryColor: '#00696B', accentColor: '#C98900',
  domains: [], enabled: true,
};

interface BrandingContextValue {
  branding: CustomerBranding;
  setBranding: (branding: CustomerBranding) => void;
}

const BrandingContext = createContext<BrandingContextValue>({ branding: defaultBranding, setBranding: () => undefined });

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(defaultBranding);

  useEffect(() => {
    let current = true;
    const resolve = httpsCallable<{ hostname: string }, CustomerBranding>(functions, 'getCustomerBrandingV2');
    resolve({ hostname: window.location.hostname })
      .then(({ data }) => { if (current) setBranding(data); })
      .catch(() => { if (current) setBranding(defaultBranding); });
    return () => { current = false; };
  }, [user?.uid]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
    document.title = branding.productName;
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]') || document.createElement('link');
    favicon.rel = 'icon';
    favicon.href = branding.logoUrl;
    if (!favicon.parentNode) document.head.appendChild(favicon);
  }, [branding]);

  return <BrandingContext.Provider value={{ branding, setBranding }}>{children}</BrandingContext.Provider>;
}

export function useBranding() { return useContext(BrandingContext); }

export function BrandLogo({ className = '', alt }: { className?: string; alt?: string }) {
  const { branding } = useBranding();
  return <img src={branding.logoUrl} alt={alt ?? branding.productName} className={className} />;
}
